/**
 * offlineStore.js — Almacén offline avanzado con IndexedDB.
 *
 * Complementa a offlineCache.js (localStorage, catálogo liviano).
 * Este módulo maneja volúmenes grandes: clientes por ciudad, productos completos,
 * memoria de precios por cliente, y cola de pedidos creados sin conexión.
 *
 * DB: "beem_offline_v1"
 * Stores:
 *   - clientes    (keyPath: id)          — clientes precargados del vendedor
 *   - productos   (keyPath: id)          — catálogo activo
 *   - precios     (keyPath: cliente_id)  — {cliente_id, precios: {producto_id: {ultimo_precio,...}}}
 *   - meta        (keyPath: key)         — {key: "visita", at, ciudades, vendedor_id, counts}
 *   - pedidos_out (keyPath: local_id, autoIncrement) — cola de pedidos creados offline
 */

const DB_NAME = "beem_offline_v1";
const DB_VERSION = 2;

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("clientes")) db.createObjectStore("clientes", { keyPath: "id" });
      if (!db.objectStoreNames.contains("productos")) db.createObjectStore("productos", { keyPath: "id" });
      if (!db.objectStoreNames.contains("precios")) db.createObjectStore("precios", { keyPath: "cliente_id" });
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "key" });
      if (!db.objectStoreNames.contains("pedidos_out")) db.createObjectStore("pedidos_out", { keyPath: "local_id", autoIncrement: true });
      // v2: blobs de imágenes de producto para trabajar 100% offline con fotos.
      // Cada entry: { key: `${producto_id}-${size}`, producto_id, size: 200|400, blob, at }
      if (!db.objectStoreNames.contains("productos_img")) db.createObjectStore("productos_img", { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function _tx(store, mode = "readonly") {
  const db = await openDB();
  return db.transaction(store, mode).objectStore(store);
}

function _wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _clear(store) {
  const s = await _tx(store, "readwrite");
  return _wrap(s.clear());
}

async function _bulkPut(store, items) {
  const s = await _tx(store, "readwrite");
  return Promise.all(items.map((it) => _wrap(s.put(it))));
}

/** Precarga completa desde el snapshot devuelto por /pedidos/_/precargar-visita */
export async function preloadVisita(snapshot) {
  const { clientes = [], productos = [], precios_por_cliente = {}, meta = {} } = snapshot || {};
  await Promise.all([_clear("clientes"), _clear("productos"), _clear("precios")]);
  await _bulkPut("clientes", clientes);
  await _bulkPut("productos", productos);
  const preciosArr = Object.entries(precios_por_cliente).map(([cliente_id, precios]) => ({ cliente_id, precios }));
  await _bulkPut("precios", preciosArr);
  const s = await _tx("meta", "readwrite");
  await _wrap(s.put({ key: "visita", ...meta }));
  return meta;
}

export async function getVisitaMeta() {
  try {
    const s = await _tx("meta", "readonly");
    return (await _wrap(s.get("visita"))) || null;
  } catch { return null; }
}

export async function getOfflineClientes() {
  try {
    const s = await _tx("clientes");
    return await _wrap(s.getAll());
  } catch { return []; }
}

export async function getOfflineProductos() {
  try {
    const s = await _tx("productos");
    return await _wrap(s.getAll());
  } catch { return []; }
}

export async function getOfflinePrecios(cliente_id) {
  try {
    const s = await _tx("precios");
    const doc = await _wrap(s.get(cliente_id));
    return doc?.precios || {};
  } catch { return {}; }
}

/** Guarda un pedido creado offline. Retorna local_id. */
export async function queueOfflinePedido(pedidoPayload) {
  const s = await _tx("pedidos_out", "readwrite");
  const local_id = await _wrap(s.add({ payload: pedidoPayload, created_at: new Date().toISOString(), synced: false }));
  return local_id;
}

export async function listOfflinePedidos() {
  try {
    const s = await _tx("pedidos_out");
    return await _wrap(s.getAll());
  } catch { return []; }
}

export async function removeOfflinePedido(local_id) {
  const s = await _tx("pedidos_out", "readwrite");
  return _wrap(s.delete(local_id));
}

export async function clearAllOffline() {
  await Promise.all([_clear("clientes"), _clear("productos"), _clear("precios"), _clear("meta"), _clear("pedidos_out"), _clear("productos_img")]);
}

// -------------------- Imágenes offline --------------------

/**
 * Descarga y almacena la miniatura de un producto en IndexedDB.
 * @param {string} apiBase - URL base del API (ej: REACT_APP_BACKEND_URL/api)
 * @param {string} producto_id
 * @param {200|400} size
 */
async function _fetchAndStoreImg(apiBase, producto_id, size) {
  try {
    const url = `${apiBase}/catalogo/img/${producto_id}?w=${size}`;
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return false;
    const blob = await res.blob();
    const s = await _tx("productos_img", "readwrite");
    await _wrap(s.put({
      key: `${producto_id}-${size}`,
      producto_id,
      size,
      blob,
      at: new Date().toISOString(),
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Descarga las miniaturas 200 y 400 de una lista de productos activos.
 * Corre en paralelo por lotes para no saturar la red.
 * @param {string} apiBase
 * @param {Array<{id: string, tiene_imagen?: boolean}>} productos
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {{ok: number, fail: number}}
 */
export async function preloadProductImages(apiBase, productos, onProgress) {
  const conFoto = (productos || []).filter((p) => p.tiene_imagen);
  const total = conFoto.length;
  let ok = 0, fail = 0, done = 0;
  const BATCH = 8;
  for (let i = 0; i < conFoto.length; i += BATCH) {
    const batch = conFoto.slice(i, i + BATCH);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.flatMap((p) => [
      _fetchAndStoreImg(apiBase, p.id, 200),
      _fetchAndStoreImg(apiBase, p.id, 400),
    ].map((pr) => pr.then((r) => { r ? ok++ : fail++; }))));
    done += batch.length;
    if (onProgress) onProgress(done, total);
  }
  return { ok, fail, total };
}

/** Devuelve un objectURL local para renderizar la imagen si está en cache; null si no. */
export async function getOfflineImageURL(producto_id, size = 200) {
  try {
    const s = await _tx("productos_img");
    const doc = await _wrap(s.get(`${producto_id}-${size}`));
    if (!doc || !doc.blob) return null;
    return URL.createObjectURL(doc.blob);
  } catch { return null; }
}

/** Cuántas fotos hay guardadas offline. */
export async function countOfflineImages() {
  try {
    const s = await _tx("productos_img");
    return await _wrap(s.count());
  } catch { return 0; }
}
